import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import PageBackButton from '../../components/PageBackButton';
import { listSymbols, runScreener } from '../../api/dataWarehouseApi';
import {
  Search, Loader2, TrendingUp, TrendingDown, Globe, Building2,
  BarChart3, Coins, Landmark, ChevronLeft, ChevronRight,
  LayoutGrid, List, Layers, Compass, DollarSign, Package,
  Flame, Target, Zap, Activity, ArrowDown, ArrowUp, Crown,
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────

function fmt(val, decimals = 2) {
  if (val == null) return '—';
  const n = Number(val);
  return Number.isNaN(n) ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtBig(val) {
  if (val == null) return '—';
  const n = Number(val);
  if (Number.isNaN(n)) return '—';
  if (Math.abs(n) >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtPct(val) {
  if (val == null) return '—';
  const n = Number(val);
  return Number.isNaN(n) ? '—' : `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;
}

// ── Constants ────────────────────────────────────────────────────

const ASSET_CLASSES = [
  { value: '', label: 'All', icon: Compass },
  { value: 'STOCK', label: 'Stocks', icon: Building2 },
  { value: 'FOREX', label: 'Forex', icon: DollarSign },
  { value: 'CRYPTO', label: 'Crypto', icon: Coins },
  { value: 'ETF', label: 'ETFs', icon: Layers },
  { value: 'COMMODITY', label: 'Commodities', icon: Package },
  { value: 'INDEX', label: 'Indices', icon: Landmark },
];

const QUICK_LISTS = [
  { label: 'Top Gainers', sort: { field: 'change_pct', direction: 'desc' }, filters: [{ field: 'change_pct', op: 'gt', value: 0 }] },
  { label: 'Top Losers', sort: { field: 'change_pct', direction: 'asc' }, filters: [{ field: 'change_pct', op: 'lt', value: 0 }] },
  { label: 'Most Active', sort: { field: 'volume_ratio', direction: 'desc' }, filters: [] },
  { label: 'Oversold (RSI < 30)', sort: { field: 'rsi_14', direction: 'asc' }, filters: [{ field: 'rsi_14', op: 'lt', value: 30 }] },
  { label: 'Overbought (RSI > 70)', sort: { field: 'rsi_14', direction: 'desc' }, filters: [{ field: 'rsi_14', op: 'gt', value: 70 }] },
  { label: 'Highest Market Cap', sort: { field: 'market_cap', direction: 'desc' }, filters: [] },
];

// ── Market Movers definitions ────────────────────────────────────
const MOVER_SECTIONS = [
  {
    key: 'gainers',
    title: 'Top Gainers',
    icon: TrendingUp,
    iconColor: 'text-success',
    gradient: 'from-success/10 to-transparent',
    sort: { field: 'change_pct', direction: 'desc' },
    filters: [{ field: 'change_pct', op: 'gt', value: 0 }],
    badge: (r) => fmtPct(r.change_pct),
    badgeColor: () => 'text-success',
  },
  {
    key: 'losers',
    title: 'Top Losers',
    icon: TrendingDown,
    iconColor: 'text-destructive',
    gradient: 'from-destructive/10 to-transparent',
    sort: { field: 'change_pct', direction: 'asc' },
    filters: [{ field: 'change_pct', op: 'lt', value: 0 }],
    badge: (r) => fmtPct(r.change_pct),
    badgeColor: () => 'text-destructive',
  },
  {
    key: 'active',
    title: 'Most Active',
    icon: Flame,
    iconColor: 'text-orange-400',
    gradient: 'from-orange-500/10 to-transparent',
    sort: { field: 'volume_ratio', direction: 'desc' },
    filters: [],
    badge: (r) => r.volume_ratio != null ? `${Number(r.volume_ratio).toFixed(1)}x vol` : fmtBig(r.volume),
    badgeColor: () => 'text-orange-400',
  },
  {
    key: 'oversold',
    title: 'Oversold',
    icon: ArrowDown,
    iconColor: 'text-blue-400',
    gradient: 'from-blue-500/10 to-transparent',
    sort: { field: 'rsi_14', direction: 'asc' },
    filters: [{ field: 'rsi_14', op: 'lt', value: 30 }],
    badge: (r) => r.rsi_14 != null ? `RSI ${Number(r.rsi_14).toFixed(1)}` : '—',
    badgeColor: () => 'text-blue-400',
  },
  {
    key: 'trending',
    title: 'Strong Trend',
    icon: Zap,
    iconColor: 'text-purple-400',
    gradient: 'from-purple-500/10 to-transparent',
    sort: { field: 'adx_14', direction: 'desc' },
    filters: [{ field: 'adx_14', op: 'gt', value: 25 }],
    badge: (r) => r.adx_14 != null ? `ADX ${Number(r.adx_14).toFixed(1)}` : '—',
    badgeColor: () => 'text-purple-400',
  },
  {
    key: 'near52h',
    title: 'Near 52W High',
    icon: Crown,
    iconColor: 'text-amber-400',
    gradient: 'from-amber-500/10 to-transparent',
    sort: { field: 'dist_from_52w_high', direction: 'desc' },
    filters: [{ field: 'dist_from_52w_high', op: 'gt', value: -5 }],
    badge: (r) => r.dist_from_52w_high != null ? `${Number(r.dist_from_52w_high).toFixed(1)}%` : '—',
    badgeColor: () => 'text-amber-400',
  },
];

// ── MoverCard sub-component ──────────────────────────────────────
function MoverCard({ row, navigate }) {
  const changePct = row?.change_pct;
  const isUp = changePct != null && Number(changePct) >= 0;
  return (
    <button
      onClick={() => navigate(`/explorer/${row.symbol}`)}
      className="flex-shrink-0 w-44 bg-backgroundSurface border border-borderDefault rounded-xl p-3
        hover:border-primary/40 hover:shadow-md hover:shadow-primary/5 transition-all duration-200 text-left"
    >
      <div className="flex items-center gap-2 mb-2">
        {row.logo_url ? (
          <img src={row.logo_url} alt="" className="w-6 h-6 rounded-md bg-backgroundInput object-contain flex-shrink-0" />
        ) : (
          <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
            <span className="text-[9px] font-bold text-primary">{(row.symbol || '?')[0]}</span>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-mono font-semibold text-xs text-primary truncate">{row.symbol}</p>
          <p className="text-[10px] text-textSecondary truncate">{row.name || row.symbol}</p>
        </div>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-bold font-mono text-textPrimary">{fmt(row.close)}</span>
        <span className={`text-xs font-semibold font-mono ${isUp ? 'text-success' : 'text-destructive'}`}>
          {fmtPct(changePct)}
        </span>
      </div>
    </button>
  );
}

// ── MoverStrip sub-component ─────────────────────────────────────
function MoverStrip({ section, data, loading, navigate }) {
  const Icon = section.icon;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 rounded-lg bg-gradient-to-br ${section.gradient}`}>
          <Icon className={`w-4 h-4 ${section.iconColor}`} />
        </div>
        <h3 className="text-sm font-semibold text-textPrimary">{section.title}</h3>
        {data.length > 0 && (
          <span className="text-xs text-textSecondary ml-1">({data.length})</span>
        )}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-6 px-2">
          <Loader2 className="w-4 h-4 animate-spin text-textSecondary" />
          <span className="text-xs text-textSecondary">Loading...</span>
        </div>
      ) : data.length === 0 ? (
        <p className="text-xs text-textSecondary py-4 px-2">No results</p>
      ) : (
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {data.map(row => (
            <MoverCard key={row.symbol} row={row} navigate={navigate} />
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// MarketExplorerPage
// ══════════════════════════════════════════════════════════════════

export default function MarketExplorerPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [assetClass, setAssetClass] = useState(searchParams.get('asset_class') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [page, setPage] = useState(1);
  const [pageSize] = useState(48);
  const [activeQuickList, setActiveQuickList] = useState(null);

  // Data
  const [symbols, setSymbols] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Screener snapshot data keyed by symbol for enrichment
  const [snapshots, setSnapshots] = useState({});

  // Market movers data
  const [movers, setMovers] = useState({});
  const [moversLoading, setMoversLoading] = useState(true);
  // Show movers on the default "home" view (no search, no quick list)
  const showMovers = !debouncedSearch && activeQuickList == null;

  const totalPages = Math.ceil(totalCount / pageSize);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch symbol list or screener-based quick list
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (activeQuickList != null) {
        // Use screener for curated lists
        const ql = QUICK_LISTS[activeQuickList];
        const body = {
          timeframe: 'D1',
          filters: ql.filters,
          asset_class: assetClass || undefined,
          sort: ql.sort,
          page,
          page_size: pageSize,
        };
        const res = await runScreener(body);
        const rows = res?.results || [];
        setSymbols(rows.map(r => ({
          symbol: r.symbol,
          name: r.name || r.symbol,
          asset_class: r.asset_class,
          exchange: r.exchange,
          sector: r.sector,
          logo_url: r.logo_url,
        })));
        // Build snapshot map
        const snap = {};
        for (const r of rows) { snap[r.symbol] = r; }
        setSnapshots(snap);
        setTotalCount(res?.total || 0);
      } else {
        // Use symbol list
        const params = { page, page_size: pageSize };
        if (assetClass) params.asset_class = assetClass;
        if (debouncedSearch) params.search = debouncedSearch;
        const res = await listSymbols(params);
        const items = res?.results || res || [];
        setSymbols(Array.isArray(items) ? items : []);
        setTotalCount(res?.count || items.length || 0);

        // Fetch screener snapshots for these symbols to get price data
        if (items.length > 0) {
          try {
            const screenerBody = {
              timeframe: 'D1',
              filters: [],
              asset_class: assetClass || undefined,
              sort: { field: 'symbol', direction: 'asc' },
              page: 1,
              page_size: 200,
            };
            if (debouncedSearch) {
              // If searching, just use what we have from symbol list
            }
            const snapRes = await runScreener(screenerBody);
            const snap = {};
            for (const r of (snapRes?.results || [])) { snap[r.symbol] = r; }
            setSnapshots(snap);
          } catch {
            // Non-critical — grid will just show without price data
          }
        }
      }
    } catch (err) {
      setError(err?.message || 'Failed to load data');
      setSymbols([]);
    } finally {
      setLoading(false);
    }
  }, [assetClass, debouncedSearch, page, pageSize, activeQuickList]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch market movers when in default view
  useEffect(() => {
    if (!showMovers) return;
    let cancelled = false;
    setMoversLoading(true);

    async function loadMovers() {
      const results = {};
      await Promise.allSettled(
        MOVER_SECTIONS.map(async (sec) => {
          try {
            const res = await runScreener({
              timeframe: 'D1',
              filters: sec.filters,
              asset_class: assetClass || undefined,
              sort: sec.sort,
              page: 1,
              page_size: 10,
            });
            if (!cancelled) {
              results[sec.key] = res?.results || [];
            }
          } catch {
            if (!cancelled) results[sec.key] = [];
          }
        })
      );
      if (!cancelled) {
        setMovers(results);
        setMoversLoading(false);
      }
    }
    loadMovers();
    return () => { cancelled = true; };
  }, [showMovers, assetClass]);

  // Sync URL params
  useEffect(() => {
    const params = {};
    if (assetClass) params.asset_class = assetClass;
    if (search) params.search = search;
    setSearchParams(params, { replace: true });
  }, [assetClass, search, setSearchParams]);

  // ── Handlers ──
  const handleAssetClassChange = (val) => {
    setAssetClass(val);
    setPage(1);
    setActiveQuickList(null);
  };

  const handleQuickList = (idx) => {
    setActiveQuickList(prev => prev === idx ? null : idx);
    setSearch('');
    setPage(1);
  };

  // ── Stat summary from snapshots ──
  const stats = (() => {
    const snaps = Object.values(snapshots);
    if (snaps.length === 0) return null;
    let gainers = 0, losers = 0, avgRsi = 0, rsiCount = 0;
    for (const s of snaps) {
      if (s.change_pct > 0) gainers++;
      if (s.change_pct < 0) losers++;
      if (s.rsi_14 != null) { avgRsi += Number(s.rsi_14); rsiCount++; }
    }
    return {
      total: snaps.length,
      gainers,
      losers,
      avgRsi: rsiCount > 0 ? (avgRsi / rsiCount).toFixed(1) : '—',
    };
  })();

  return (
    <div className="my-2 sm:my-4 md:my-6 mx-auto max-w-[100rem] space-y-4">
      {/* ── Hero Header ── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-backgroundSurface to-backgroundSurface border border-borderDefault p-6 sm:p-8">
        <div className="relative z-10">
          <div className="mb-4">
            <PageBackButton label="Back to Research" to="/research" preferHistory={false} hideLabelOnMobile />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-textPrimary">
            Market Explorer
          </h1>
          <p className="text-sm sm:text-base text-textSecondary mt-1 max-w-xl">
            Browse, discover and analyze {totalCount > 0 ? totalCount.toLocaleString() : ''} instruments with real-time technical and fundamental data.
          </p>

          {/* Search */}
          <div className="relative mt-4 max-w-lg">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-textSecondary pointer-events-none z-10" />
            <input
              type="text"
              placeholder="Search by symbol, name, or sector..."
              value={search}
              onChange={e => { setSearch(e.target.value); setActiveQuickList(null); }}
              className="w-full pl-12 pr-10 py-3 rounded-xl border border-borderDefault bg-backgroundSurface text-textPrimary
                placeholder:text-textSecondary/60 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary
                transition text-sm sm:text-base"
            />
            {search && (
              <button onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-textSecondary hover:text-textPrimary transition">
                ×
              </button>
            )}
          </div>
        </div>

        {/* Summary stats */}
        {stats && (
          <div className="absolute top-6 right-6 hidden lg:flex items-center gap-6 text-sm">
            <div className="text-center">
              <p className="text-2xl font-bold text-textPrimary">{stats.total}</p>
              <p className="text-textSecondary text-xs">Instruments</p>
            </div>
            <div className="w-px h-10 bg-borderDefault" />
            <div className="text-center">
              <p className="text-2xl font-bold text-success">{stats.gainers}</p>
              <p className="text-textSecondary text-xs">Gainers</p>
            </div>
            <div className="w-px h-10 bg-borderDefault" />
            <div className="text-center">
              <p className="text-2xl font-bold text-destructive">{stats.losers}</p>
              <p className="text-textSecondary text-xs">Losers</p>
            </div>
            <div className="w-px h-10 bg-borderDefault" />
            <div className="text-center">
              <p className="text-2xl font-bold text-primary">{stats.avgRsi}</p>
              <p className="text-textSecondary text-xs">Avg RSI</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Asset Class Tabs ── */}
      <div className="flex gap-1 flex-wrap bg-backgroundSurface rounded-xl border border-borderDefault p-1">
        {ASSET_CLASSES.map(ac => {
          const Icon = ac.icon;
          return (
            <button key={ac.value}
              onClick={() => handleAssetClassChange(ac.value)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition whitespace-nowrap ${
                assetClass === ac.value ? 'bg-primary text-textAccent' : 'text-textSecondary hover:bg-primary/10'
              }`}
            >
              <Icon className="w-4 h-4" />
              {ac.label}
            </button>
          );
        })}
      </div>

      {/* ── Quick Lists + View toggle ── */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-textSecondary font-medium mr-1">Quick:</span>
        {QUICK_LISTS.map((ql, i) => (
          <button key={i} onClick={() => handleQuickList(i)}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition ${
              activeQuickList === i
                ? 'bg-primary/15 border-primary/40 text-primary'
                : 'border-borderDefault bg-backgroundSurface hover:bg-primary/10 hover:border-primary/20 text-textSecondary'
            }`}
          >
            {ql.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1 bg-backgroundSurface border border-borderDefault rounded-lg p-0.5">
          <button onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded-md transition ${viewMode === 'grid' ? 'bg-primary text-textAccent' : 'text-textSecondary hover:text-textPrimary'}`}
            title="Grid view">
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button onClick={() => setViewMode('list')}
            className={`p-1.5 rounded-md transition ${viewMode === 'list' ? 'bg-primary text-textAccent' : 'text-textSecondary hover:text-textPrimary'}`}
            title="List view">
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ── Market Movers ── */}
      {showMovers && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-bold text-textPrimary">Market Movers</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {MOVER_SECTIONS.map(sec => (
              <Card key={sec.key} className="overflow-hidden">
                <CardContent className="p-3 sm:p-4">
                  <MoverStrip
                    section={sec}
                    data={movers[sec.key] || []}
                    loading={moversLoading}
                    navigate={navigate}
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ── Loading ── */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-7 h-7 animate-spin text-primary mr-3" />
          <span className="text-textSecondary">Loading instruments...</span>
        </div>
      )}

      {/* ── Error ── */}
      {error && !loading && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive mb-3">{error}</p>
            <Button variant="outline" size="sm" onClick={fetchData}>Retry</Button>
          </CardContent>
        </Card>
      )}

      {/* ── Empty ── */}
      {!loading && !error && symbols.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <Globe className="w-12 h-12 text-textSecondary/30 mx-auto mb-3" />
            <p className="text-textSecondary">No instruments found. Try adjusting your search or filters.</p>
          </CardContent>
        </Card>
      )}

      {/* ── Browse section heading ── */}
      {!loading && !error && symbols.length > 0 && showMovers && (
        <div className="flex items-center gap-2 pt-2">
          <Globe className="w-5 h-5 text-primary" />
          <h2 className="text-lg font-bold text-textPrimary">Browse All Instruments</h2>
          <span className="text-sm text-textSecondary">({totalCount.toLocaleString()})</span>
        </div>
      )}

      {/* ── Grid View ── */}
      {!loading && !error && symbols.length > 0 && viewMode === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
          {symbols.map(s => {
            const snap = snapshots[s.symbol];
            const changePct = snap?.change_pct;
            const isUp = changePct != null && Number(changePct) >= 0;
            return (
              <button key={s.symbol}
                onClick={() => navigate(`/explorer/${s.symbol}`)}
                className="group text-left bg-backgroundSurface border border-borderDefault rounded-xl p-4
                  hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-200">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {s.logo_url ? (
                      <img src={s.logo_url} alt="" className="w-8 h-8 rounded-lg bg-backgroundInput object-contain flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-primary">{(s.symbol || '?')[0]}</span>
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-mono font-semibold text-sm text-primary truncate">{s.symbol}</p>
                      <p className="text-xs text-textSecondary truncate">{s.name}</p>
                    </div>
                  </div>
                  {s.asset_class && (
                    <Badge className="text-[10px] flex-shrink-0">{s.asset_class}</Badge>
                  )}
                </div>

                {/* Price data from snapshot */}
                {snap ? (
                  <>
                    <div className="flex items-baseline justify-between mb-2">
                      <span className="text-lg font-bold font-mono text-textPrimary">{fmt(snap.close)}</span>
                      <span className={`text-sm font-semibold font-mono ${isUp ? 'text-success' : 'text-destructive'}`}>
                        {fmtPct(changePct)}
                      </span>
                    </div>

                    {/* Mini metrics row */}
                    <div className="grid grid-cols-3 gap-2 text-xs text-textSecondary">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider opacity-60">RSI</p>
                        <p className={`font-mono font-medium ${
                          snap.rsi_14 != null && snap.rsi_14 < 30 ? 'text-success' :
                          snap.rsi_14 != null && snap.rsi_14 > 70 ? 'text-destructive' : 'text-textPrimary'
                        }`}>
                          {snap.rsi_14 != null ? Number(snap.rsi_14).toFixed(1) : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider opacity-60">Vol</p>
                        <p className="font-mono font-medium text-textPrimary">{fmtBig(snap.volume)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider opacity-60">MCap</p>
                        <p className="font-mono font-medium text-textPrimary">{snap.market_cap ? fmtBig(snap.market_cap) : '—'}</p>
                      </div>
                    </div>

                    {/* Sector / Exchange */}
                    {(s.sector || s.exchange) && (
                      <div className="mt-2 pt-2 border-t border-borderDefault/50 flex items-center gap-1.5 text-[10px] text-textSecondary">
                        {s.exchange && <span className="bg-backgroundInput px-1.5 py-0.5 rounded">{s.exchange}</span>}
                        {s.sector && <span className="truncate">{s.sector}</span>}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-1 mt-1">
                    {(s.sector || s.exchange) && (
                      <div className="flex items-center gap-1.5 text-xs text-textSecondary">
                        {s.exchange && <span className="bg-backgroundInput px-1.5 py-0.5 rounded text-[10px]">{s.exchange}</span>}
                        {s.sector && <span className="truncate">{s.sector}</span>}
                      </div>
                    )}
                    {s.industry && <p className="text-[10px] text-textSecondary truncate">{s.industry}</p>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ── List View ── */}
      {!loading && !error && symbols.length > 0 && viewMode === 'list' && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-borderDefault bg-backgroundInput/50 text-xs text-textSecondary">
                    <th className="text-left py-2.5 px-4 font-semibold w-28">Symbol</th>
                    <th className="text-left py-2.5 px-3 font-semibold min-w-[140px]">Name</th>
                    <th className="text-left py-2.5 px-3 font-semibold">Class</th>
                    <th className="text-left py-2.5 px-3 font-semibold">Exchange</th>
                    <th className="text-left py-2.5 px-3 font-semibold">Sector</th>
                    <th className="text-right py-2.5 px-3 font-semibold">Price</th>
                    <th className="text-right py-2.5 px-3 font-semibold">Change %</th>
                    <th className="text-right py-2.5 px-3 font-semibold">RSI</th>
                    <th className="text-right py-2.5 px-3 font-semibold">ADX</th>
                    <th className="text-right py-2.5 px-3 font-semibold">Volume</th>
                    <th className="text-right py-2.5 px-3 font-semibold">Mkt Cap</th>
                  </tr>
                </thead>
                <tbody>
                  {symbols.map(s => {
                    const snap = snapshots[s.symbol];
                    const changePct = snap?.change_pct;
                    const isUp = changePct != null && Number(changePct) >= 0;
                    return (
                      <tr key={s.symbol}
                        onClick={() => navigate(`/explorer/${s.symbol}`)}
                        className="border-b border-borderDefault/50 hover:bg-primary/5 transition cursor-pointer">
                        <td className="py-2.5 px-4">
                          <div className="flex items-center gap-2">
                            {s.logo_url ? (
                              <img src={s.logo_url} alt="" className="w-6 h-6 rounded bg-backgroundInput object-contain" />
                            ) : (
                              <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center">
                                <span className="text-[9px] font-bold text-primary">{(s.symbol || '?')[0]}</span>
                              </div>
                            )}
                            <span className="font-mono font-semibold text-primary">{s.symbol}</span>
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-textSecondary truncate max-w-[200px]">{s.name}</td>
                        <td className="py-2.5 px-3"><Badge className="text-[10px]">{s.asset_class}</Badge></td>
                        <td className="py-2.5 px-3 text-textSecondary">{s.exchange || '—'}</td>
                        <td className="py-2.5 px-3 text-textSecondary truncate max-w-[120px]">{s.sector || '—'}</td>
                        <td className="py-2.5 px-3 text-right font-mono text-textPrimary">{snap ? fmt(snap.close) : '—'}</td>
                        <td className={`py-2.5 px-3 text-right font-mono font-semibold ${
                          isUp ? 'text-success' : changePct != null ? 'text-destructive' : 'text-textSecondary'
                        }`}>
                          {snap ? fmtPct(changePct) : '—'}
                        </td>
                        <td className={`py-2.5 px-3 text-right font-mono ${
                          snap?.rsi_14 != null && snap.rsi_14 < 30 ? 'text-success' :
                          snap?.rsi_14 != null && snap.rsi_14 > 70 ? 'text-destructive' : 'text-textPrimary'
                        }`}>
                          {snap?.rsi_14 != null ? Number(snap.rsi_14).toFixed(1) : '—'}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-textPrimary">
                          {snap?.adx_14 != null ? Number(snap.adx_14).toFixed(1) : '—'}
                        </td>
                        <td className="py-2.5 px-3 text-right font-mono text-textPrimary">{snap ? fmtBig(snap.volume) : '—'}</td>
                        <td className="py-2.5 px-3 text-right font-mono text-textPrimary">{snap?.market_cap ? fmtBig(snap.market_cap) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Pagination ── */}
      {!loading && totalCount > pageSize && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-textSecondary">
            {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, totalCount)} of {totalCount.toLocaleString()}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
              let p;
              if (totalPages <= 7) p = i + 1;
              else if (page <= 4) p = i + 1;
              else if (page >= totalPages - 3) p = totalPages - 6 + i;
              else p = page - 3 + i;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 text-sm rounded-lg transition ${
                    page === p ? 'bg-primary text-textAccent font-semibold' : 'text-textSecondary hover:bg-primary/10'
                  }`}>
                  {p}
                </button>
              );
            })}
            <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
